export interface SiteColorSource {
  color?: string | null;
}

export type SiteColorStyle = Record<string, string>;

export interface SiteGradient {
  start: string;
  end: string;
  shadow: string;
}

export type SiteColorMap = Record<string | number, SiteColorSource | undefined>;

export function normalizeHexColor(color?: string | null): string | undefined {
  if (!color) {
    return undefined;
  }

  const value = color.trim().replace(/^#/, "");
  if (/^[0-9a-fA-F]{3}$/.test(value)) {
    return `#${value
      .split("")
      .map((character) => character + character)
      .join("")
      .toLowerCase()}`;
  }

  if (/^[0-9a-fA-F]{6}$/.test(value)) {
    return `#${value.toLowerCase()}`;
  }

  return undefined;
}

export function getSiteColor(
  sitesMap: SiteColorMap | null | undefined,
  siteID: string | number | null | undefined
): string | undefined {
  if (!sitesMap || siteID == null) {
    return undefined;
  }

  return normalizeHexColor(sitesMap[siteID]?.color);
}

export function getContrastingTextColor(backgroundColor: string): "#000000" | "#ffffff" {
  const normalizedColor = normalizeHexColor(backgroundColor);
  if (!normalizedColor) {
    return "#ffffff";
  }

  const channels = [1, 3, 5].map((offset) => {
    const channel = parseInt(normalizedColor.slice(offset, offset + 2), 16) / 255;
    return channel <= 0.04045
      ? channel / 12.92
      : Math.pow((channel + 0.055) / 1.055, 2.4);
  });
  const luminance = 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
  const blackContrast = (luminance + 0.05) / 0.05;
  const whiteContrast = 1.05 / (luminance + 0.05);

  return blackContrast >= whiteContrast ? "#000000" : "#ffffff";
}

export function getSiteColorStyle(
  sitesMap: SiteColorMap | null | undefined,
  siteID: string | number | null | undefined
): SiteColorStyle {
  const backgroundColor = getSiteColor(sitesMap, siteID);
  if (!backgroundColor) {
    return {};
  }

  return {
    backgroundColor,
    color: getContrastingTextColor(backgroundColor),
  };
}

export function getSiteGradient(color?: string | null, lightenBy = 0.15): SiteGradient | undefined {
  const start = normalizeHexColor(color);
  if (!start) {
    return undefined;
  }

  const amount = Math.min(1, Math.max(0, lightenBy));
  const channels = [1, 3, 5].map((offset) => {
    const channel = parseInt(start.slice(offset, offset + 2), 16);
    return Math.round(channel + (255 - channel) * amount);
  });
  const end = `#${channels.map((channel) => channel.toString(16).padStart(2, "0")).join("")}`;

  return { start, end, shadow: start };
}
